import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import DateSelector from "@cayuse/shared-react.components.date-selector";

import VerticalSpacing from "@cayuse/shared-react.components.vertical-spacing";
import { isNonEmptyArray, isNonEmptyObject, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";

import StatusDropdown from "components/ProjectSummary/StatusDropdown";
import StatusCommentModal from "components/StatusCommentModal";
import Table from "components/Table";
import {
	COLUMN_HEADER_KEYS,
	ROLE_SP_SYSTEM_ADMINISTRATOR,
	ROLE_SP_AWARD_ADMINISTRATOR,
	ROLE_SP_PROPOSAL_ADMINISTRATOR,
} from "globals/constants";
import { useDictionary, useResolutions, useHasRoles } from "hooks";
import { getProject } from "store/actions/project";
import { projectSelectors, tenantSelectors } from "store/selectors";
import { slugifyString, mapProjectSummary, notifyError } from "utils";
import { api } from "services";
import EditableTitle from './EditableTitle';

import styles from "./styles.css";


const optionalCommentOverrides = {
	"in-development": [ "active" ],
	active: [ "in-closeout" ],
	"in-closeout": [ "closed" ],
	closed: []
};

const proposalAdminStatuses = [ "closed", "in-development" ];

const ProjectSummary = ({ hasAwards }) => {
	const dictionary = useDictionary("projects.summary");
	const project = useSelector(projectSelectors.project);
	const { projectStatuses } = useSelector(tenantSelectors.tenant);

	const [ selectedDate, setSelectedDate ] = useState(() => {
		return isNonEmptyString(project.createdOn)
			? new Date(project.createdOn)
			: null;
	});
	const [ statusOverrideId, setStatusOverrideId ] = useState(null);
	const [ isStatusLoading, setIsStatusLoading ] = useState(false);
	const [ statusSelected, setStatusSelected ] = useState(null);

	const [ commentModalVisible, setCommentModalVisible ] = useState(false);
	const [ commentModalTitle, setCommentModalTitle ] = useState("");
	const [ commentOptional, setCommentOptional ] = useState(false);
	const [ comment, setComment ] = useState("");

	const [ resolutionOptions, setResolutionOptions ] = useState([]);
	const [ resolutionSelected, setResolutionSelected ] = useState(null);
	const [ resolutionsVisible, setResolutionsVisible ] = useState(false);
	const resolutions = useResolutions(COLUMN_HEADER_KEYS.PROJECT);

	const {
		[ROLE_SP_SYSTEM_ADMINISTRATOR]: isSystemAdmin,
		[ROLE_SP_AWARD_ADMINISTRATOR]: isAwardAdmin,
		[ROLE_SP_PROPOSAL_ADMINISTRATOR]: isProposalAdmin
	} = useHasRoles([
		ROLE_SP_SYSTEM_ADMINISTRATOR,
		ROLE_SP_AWARD_ADMINISTRATOR,
		ROLE_SP_PROPOSAL_ADMINISTRATOR
	]);
	const canEditStatus = (hasAwards && isAwardAdmin) || (!hasAwards && (isProposalAdmin || isAwardAdmin));
	const isDropdownActive = canEditStatus || isSystemAdmin;

	const onCommentOpen = (fromStatus, toStatus, toStatusId) => {
		const isResolutionVisible = (toStatus === "Closed");
		if (fromStatus !== toStatus) {
			if (optionalCommentOverrides[slugifyString(fromStatus)]?.includes(toStatusId)) {
				setCommentOptional(true);
			}else{
				setCommentOptional(false);
			}
			setCommentModalTitle(`Change Project Status: ${fromStatus} to ${toStatus}`);
			setStatusOverrideId(toStatusId);
			setComment("");
			setResolutionSelected(null);
			setResolutionsVisible(isResolutionVisible);
			setCommentModalVisible(true);
			setStatusSelected(toStatus);
		}
	};

	const onCommentClose = (actionType = "close") => {
		setCommentModalVisible(false);
		if (actionType !== "confirm") {
			if (isNonEmptyObject(project) && isNonEmptyString(project.status) && isNonEmptyArray(projectStatuses)) {
				let status = projectStatuses.find(element => element.id === project.status);
				if (isNonEmptyObject(status)) {
					setStatusSelected(status.description);
				} else {	//TODO: this is a workaround for a backend bug, change after bug is fixed
					status = projectStatuses.find(element => element.description === project.status);
					if (isNonEmptyObject(status)) {
						setStatusSelected(status.description);
					}
				}
			}
		}
	};

	const onConfirm = async () => {
		try {
			setIsStatusLoading(true);

			const projectId = project.projectId;

			await api.project.updateStatus(
				{ id: projectId },
				{
					id: statusOverrideId,
					comment: comment,
					resolution: resolutionSelected,
				},
			);

			onCommentClose("confirm");
			getProject(projectId);

		} catch (error) {
			notifyError("Something went wrong. Please try again.", error);

		} finally {
			setIsStatusLoading(false);
		}
	};

	const statusOptions = useMemo(() => {
		if (!isNonEmptyArray(projectStatuses)) {
			return [];
		}

		return projectStatuses.map(item => {
			const isStatusForProposalAdmin = proposalAdminStatuses.includes(item.id);
			const isCurrentStatusForProposalAdmin = proposalAdminStatuses.includes(slugifyString(project.status));
			const noAwardsAndProposalAdmin = !hasAwards
					&& isProposalAdmin
					&& isStatusForProposalAdmin
					&& isCurrentStatusForProposalAdmin;
			const isSelectedStatus = statusSelected === item.description;

			const enabled = (isSystemAdmin || isAwardAdmin || noAwardsAndProposalAdmin) && !isSelectedStatus;

			return {
				className: styles.statusOverride,
				key: item.id,
				value: item.description,
				text: item.description,
				disabled: !enabled,
				onClick: () => enabled && onCommentOpen(statusSelected, item.description, item.id)
			};
		});
	}, [ isSystemAdmin, isAwardAdmin, isProposalAdmin, project.status, projectStatuses, hasAwards, statusSelected ]);

	useEffect(() => {
		if (isNonEmptyObject(project) && isNonEmptyString(project.status) && isNonEmptyArray(projectStatuses)) {
			let status = projectStatuses.find(element => element.id === project.status);
			if (isNonEmptyObject(status)) {
				setStatusSelected(status.description);
			} else {	//TODO: this is a workaround for a backend bug, change after bug is fixed
				status = projectStatuses.find(element => element.description === project.status);
				if (isNonEmptyObject(status)) {
					setStatusSelected(status.description);
				}
			}
		}
	}, [ project, projectStatuses ]);

	useEffect(() => {
		if (resolutions) {
			const options = resolutions
				.filter(resolution => resolution.active)
				.sort((a, b) => (a.value > b.value) ? 1 : -1)
				.map(item => {
					return {
						key: item.value,
						value: item.value,
						label: item.value
					};
				});
			setResolutionOptions(options);
		}
	}, [ resolutions ]);

	const summary = useMemo(() => mapProjectSummary(project.summary), [ project ]);

	return (
		<div>
			<StatusCommentModal
				comment={comment}
				title={commentModalTitle}
				optional={commentOptional}
				resolutions={resolutionOptions}
				resolutionSelected={resolutionSelected}
				resolutionsVisible={resolutionsVisible}
				visible={commentModalVisible}
				onClose={onCommentClose}
				onConfirm={onConfirm}
				onCommentChange={value => setComment(value)}
				onResolutionChange={value => setResolutionSelected(value)}
				isLoading={isStatusLoading}
				formType={COLUMN_HEADER_KEYS.AWARD}
			/>
			<div data-component="ProjectSummary">
				<VerticalSpacing>
					<Table role="presentation" className={styles.summary} celled overflow="visible" captionText="Project Summary">
						<Table.Body>
							<Table.Row>
								<Table.Cell  colSpan= "1">
									<div className={styles.summaryHeadingLayout}>
										<span className={styles.label}>
											{dictionary.projectInformation || "Project Information"}
										</span>
										{project.status &&
											<StatusDropdown
												options={statusOptions}
												value={statusSelected}
												disabled={!isDropdownActive}
												ariaLabel="Select project status"
												label="Project status:"
											/>
										}
									</div>
								</Table.Cell>
							</Table.Row>
							<Table.Row>
								<Table.Cell>
									<div className={styles.summaryLineWrapper}>
										<EditableTitle
											title={project.title}
											isAdmin={isSystemAdmin}
											projectId={project.projectId}
										/>
									</div>
									<div className={styles.summaryLineWrapper}>
										<span className={styles.label}>{"PI:"}</span>
										<span className={styles.value}>{summary.principalInvestigator}</span>
									</div>
									<div className={styles.summaryLineWrapper}>
										<span className={styles.label}>{"Admin Unit:"}</span>
										<span className={styles.value}>{summary.adminUnit}</span>
									</div>
									<div className={styles.summaryLineWrapper}>
										<span className={styles.label}>{"Sponsor:"}</span>
										<span className={styles.value}>{summary.sponsor}</span>
									</div>
									<div className={styles.summaryLineWrapper}>
										<span className={styles.label}>{"Prime Sponsor:"}</span>
										<span className={styles.value}>{summary.primeSponsor}</span>
									</div>
								</Table.Cell>
							</Table.Row>
							<Table.Row>
								<Table.Cell>
									<div className={styles.summaryLineWrapper}>
										<label className={styles.label}>{"Start Date:"}</label>
										<span className={styles.value}>
											<DateSelector
												customInputClass={`${styles.dateSelector} ${styles.value}`}
												onChange={setSelectedDate}
												selectedDate={selectedDate}
											/>
										</span>
										
									</div>
									<div className={styles.summaryLineWrapper}>
										<span className={styles.label}>{"End Date:"}</span>
										<span className={styles.value}>{"--"}</span>
									</div>
								</Table.Cell>
							</Table.Row>
						</Table.Body>
					</Table>
				</VerticalSpacing>
			</div>
		</div>
	);
};

export default ProjectSummary;
